/**
 * geo — ISO-3166 country names + major-city coordinates for the Analytics maps
 * (FEAT-ANALYTICS-001). Our `country` field is clean ISO-2; this turns it into a
 * display name for Demographics + the map, and resolves city names to lat/lng so
 * realtime traffic can be plotted as bubbles. Cities we can't resolve still show
 * in the city list — they're just not dotted on the map.
 */
export const CODE2NAME = {
  AF: 'Afghanistan', AL: 'Albania', DZ: 'Algeria', AO: 'Angola', AR: 'Argentina', AM: 'Armenia',
  AU: 'Australia', AT: 'Austria', AZ: 'Azerbaijan', BH: 'Bahrain', BD: 'Bangladesh', BY: 'Belarus',
  BE: 'Belgium', BJ: 'Benin', BO: 'Bolivia', BA: 'Bosnia & Herzegovina', BR: 'Brazil', BG: 'Bulgaria',
  KH: 'Cambodia', CM: 'Cameroon', CA: 'Canada', CL: 'Chile', CN: 'China', CO: 'Colombia',
  CR: 'Costa Rica', HR: 'Croatia', CU: 'Cuba', CY: 'Cyprus', CZ: 'Czechia', DK: 'Denmark',
  DO: 'Dominican Republic', EC: 'Ecuador', EG: 'Egypt', SV: 'El Salvador', EE: 'Estonia', ET: 'Ethiopia',
  FI: 'Finland', FR: 'France', GE: 'Georgia', DE: 'Germany', GH: 'Ghana', GR: 'Greece',
  GT: 'Guatemala', HN: 'Honduras', HK: 'Hong Kong', HU: 'Hungary', IS: 'Iceland', IN: 'India',
  ID: 'Indonesia', IR: 'Iran', IQ: 'Iraq', IE: 'Ireland', IL: 'Israel', IT: 'Italy',
  JM: 'Jamaica', JP: 'Japan', JO: 'Jordan', KZ: 'Kazakhstan', KE: 'Kenya', KW: 'Kuwait',
  KG: 'Kyrgyzstan', LA: 'Laos', LV: 'Latvia', LB: 'Lebanon', LY: 'Libya', LT: 'Lithuania',
  LU: 'Luxembourg', MO: 'Macau', MG: 'Madagascar', MY: 'Malaysia', MV: 'Maldives', ML: 'Mali',
  MT: 'Malta', MX: 'Mexico', MD: 'Moldova', MN: 'Mongolia', ME: 'Montenegro', MA: 'Morocco',
  MM: 'Myanmar', NP: 'Nepal', NL: 'Netherlands', NZ: 'New Zealand', NI: 'Nicaragua', NE: 'Niger',
  NG: 'Nigeria', MK: 'North Macedonia', NO: 'Norway', OM: 'Oman', PK: 'Pakistan', PA: 'Panama',
  PY: 'Paraguay', PE: 'Peru', PH: 'Philippines', PL: 'Poland', PT: 'Portugal', QA: 'Qatar',
  RO: 'Romania', RU: 'Russia', RW: 'Rwanda', SA: 'Saudi Arabia', SN: 'Senegal', RS: 'Serbia',
  SG: 'Singapore', SK: 'Slovakia', SI: 'Slovenia', ZA: 'South Africa', KR: 'South Korea', ES: 'Spain',
  LK: 'Sri Lanka', SD: 'Sudan', SE: 'Sweden', CH: 'Switzerland', SY: 'Syria', TW: 'Taiwan',
  TJ: 'Tajikistan', TZ: 'Tanzania', TH: 'Thailand', TN: 'Tunisia', TR: 'Türkiye', TM: 'Turkmenistan',
  UG: 'Uganda', UA: 'Ukraine', AE: 'United Arab Emirates', GB: 'United Kingdom', US: 'United States',
  UY: 'Uruguay', UZ: 'Uzbekistan', VE: 'Venezuela', VN: 'Vietnam', YE: 'Yemen', ZM: 'Zambia', ZW: 'Zimbabwe',
};

/** Display a country: ISO-2 → full name, else the raw value. */
export function countryName(code) {
  if (!code) return '(not set)';
  const c = String(code).trim().toUpperCase();
  return CODE2NAME[c] || code;
}

// Major-city coordinates [lat, lng]. Keyed lowercase. Covers the cities in our
// seed data plus global majors; extend freely.
export const CITY_LATLNG = {
  karachi: [24.86, 67.01], lahore: [31.55, 74.34], islamabad: [33.69, 73.06], rawalpindi: [33.6, 73.04],
  faisalabad: [31.42, 73.08], multan: [30.16, 71.52], peshawar: [34.01, 71.58], quetta: [30.18, 66.98],
  'new york': [40.71, -74.01], 'los angeles': [34.05, -118.24], chicago: [41.88, -87.63], houston: [29.76, -95.37],
  ashburn: [39.04, -77.49], columbus: [39.96, -82.99], 'san francisco': [37.77, -122.42], seattle: [47.61, -122.33],
  'san jose': [37.34, -121.89], dallas: [32.78, -96.8], atlanta: [33.75, -84.39], boston: [42.36, -71.06],
  miami: [25.76, -80.19], toronto: [43.65, -79.38], vancouver: [49.28, -123.12], montreal: [45.5, -73.57],
  london: [51.51, -0.13], manchester: [53.48, -2.24], birmingham: [52.49, -1.89], 'dublin': [53.35, -6.26],
  paris: [48.86, 2.35], berlin: [52.52, 13.41], madrid: [40.42, -3.7], barcelona: [41.39, 2.17],
  rome: [41.9, 12.5], amsterdam: [52.37, 4.9], brussels: [50.85, 4.35], zurich: [47.37, 8.54],
  moscow: [55.76, 37.62], istanbul: [41.01, 28.98], dubai: [25.2, 55.27], 'abu dhabi': [24.45, 54.38],
  riyadh: [24.71, 46.68], jeddah: [21.49, 39.19], 'kuwait city': [29.38, 47.99], doha: [25.29, 51.53],
  cairo: [30.04, 31.24], 'tel aviv': [32.09, 34.78], beirut: [33.89, 35.5], amman: [31.95, 35.93],
  mumbai: [19.08, 72.88], delhi: [28.7, 77.1], bangalore: [12.97, 77.59], hyderabad: [17.39, 78.49],
  chennai: [13.08, 80.27], kolkata: [22.57, 88.36], pune: [18.52, 73.86], ahmedabad: [23.03, 72.58],
  dhaka: [23.81, 90.41], colombo: [6.93, 79.86], kathmandu: [27.72, 85.32], kabul: [34.56, 69.21],
  beijing: [39.9, 116.41], shanghai: [31.23, 121.47], guangzhou: [23.13, 113.26], shenzhen: [22.54, 114.06],
  'hong kong': [22.32, 114.17], taipei: [25.03, 121.57], tokyo: [35.68, 139.69], osaka: [34.69, 135.5],
  seoul: [37.57, 126.98], singapore: [1.35, 103.82], 'kuala lumpur': [3.14, 101.69], bangkok: [13.76, 100.5],
  jakarta: [-6.21, 106.85], manila: [14.6, 120.98], 'ho chi minh city': [10.82, 106.63], hanoi: [21.03, 105.85],
  sydney: [-33.87, 151.21], melbourne: [-37.81, 144.96], brisbane: [-27.47, 153.03], perth: [-31.95, 115.86],
  auckland: [-36.85, 174.76], johannesburg: [-26.2, 28.05], 'cape town': [-33.92, 18.42], lagos: [6.52, 3.38],
  nairobi: [-1.29, 36.82], 'sao paulo': [-23.55, -46.63], 'rio de janeiro': [-22.91, -43.17], 'mexico city': [19.43, -99.13],
  'buenos aires': [-34.6, -58.38], lima: [-12.05, -77.04], bogota: [4.71, -74.07], santiago: [-33.45, -70.67],
};

/** Resolve a city name → {lat, lon} for the map, or null. */
export function cityLatLng(name) {
  if (!name) return null;
  const k = String(name).trim().toLowerCase();
  const p = CITY_LATLNG[k];
  return p ? { lat: p[0], lon: p[1] } : null;
}

// Country centroids [lat, lng], keyed by ISO-2 — for plotting country-level
// traffic on the tiled map when we only know the country (no city).
export const COUNTRY_LATLNG = {
  AF: [33.9, 67.7], AL: [41.2, 20.2], DZ: [28, 1.7], AO: [-11.2, 17.9], AR: [-38.4, -63.6], AM: [40.1, 45],
  AU: [-25.3, 133.8], AT: [47.5, 14.6], AZ: [40.1, 47.6], BH: [26, 50.6], BD: [23.7, 90.4], BY: [53.7, 27.9],
  BE: [50.5, 4.5], BJ: [9.3, 2.3], BO: [-16.3, -63.6], BA: [43.9, 17.7], BR: [-14.2, -51.9], BG: [42.7, 25.5],
  KH: [12.6, 104.9], CM: [7.4, 12.4], CA: [56.1, -106.3], CL: [-35.7, -71.5], CN: [35.9, 104.2], CO: [4.6, -74.3],
  CR: [9.7, -83.8], HR: [45.1, 15.2], CY: [35.1, 33.4], CZ: [49.8, 15.5], DK: [56.3, 9.5], DO: [18.7, -70.2],
  EC: [-1.8, -78.2], EG: [26.8, 30.8], SV: [13.8, -88.9], EE: [58.6, 25], ET: [9.1, 40.5], FI: [61.9, 25.7],
  FR: [46.2, 2.2], GE: [42.3, 43.4], DE: [51.2, 10.5], GH: [7.9, -1], GR: [39.1, 21.8], GT: [15.8, -90.2],
  HK: [22.3, 114.2], HU: [47.2, 19.5], IS: [65, -19], IN: [20.6, 78.9], ID: [-0.8, 113.9], IR: [32.4, 53.7],
  IQ: [33.2, 43.7], IE: [53.4, -8.2], IL: [31.0, 34.9], IT: [41.9, 12.6], JM: [18.1, -77.3], JP: [36.2, 138.3],
  JO: [30.6, 36.2], KZ: [48, 66.9], KE: [-0.02, 37.9], KW: [29.3, 47.5], KG: [41.2, 74.8], LA: [19.9, 102.5],
  LV: [56.9, 24.6], LB: [33.9, 35.9], LY: [26.3, 17.2], LT: [55.2, 23.9], LU: [49.8, 6.1], MO: [22.2, 113.5],
  MY: [4.2, 101.9], MV: [3.2, 73.2], MT: [35.9, 14.4], MX: [23.6, -102.6], MD: [47.4, 28.4], MN: [46.9, 103.8],
  MA: [31.8, -7.1], MM: [21.9, 95.96], NP: [28.4, 84.1], NL: [52.1, 5.3], NZ: [-40.9, 174.9], NG: [9.1, 8.7],
  MK: [41.6, 21.7], NO: [60.5, 8.5], OM: [21.5, 55.9], PK: [30.4, 69.3], PA: [8.5, -80.8], PY: [-23.4, -58.4],
  PE: [-9.2, -75], PH: [12.9, 121.8], PL: [51.9, 19.1], PT: [39.4, -8.2], QA: [25.4, 51.2], RO: [45.9, 25],
  RU: [61.5, 105.3], RW: [-1.9, 29.9], SA: [23.9, 45.1], SN: [14.5, -14.5], RS: [44.0, 21.0], SG: [1.35, 103.8],
  SK: [48.7, 19.7], SI: [46.2, 15], ZA: [-30.6, 22.9], KR: [35.9, 127.8], ES: [40.5, -3.7], LK: [7.9, 80.8],
  SD: [12.9, 30.2], SE: [60.1, 18.6], CH: [46.8, 8.2], SY: [34.8, 38.996], TW: [23.7, 121], TJ: [38.9, 71.3],
  TZ: [-6.4, 34.9], TH: [15.9, 101], TN: [33.9, 9.6], TR: [38.96, 35.2], TM: [38.97, 59.6], UG: [1.4, 32.3],
  UA: [48.4, 31.2], AE: [23.4, 53.8], GB: [55.4, -3.4], US: [37.1, -95.7], UY: [-32.5, -55.8], UZ: [41.4, 64.6],
  VE: [6.4, -66.6], VN: [14.06, 108.3], YE: [15.6, 48.5], ZM: [-13.1, 27.8], ZW: [-19, 29.2],
};

/** Country ISO-2 → [lat, lng] centroid, or null. */
export function countryLatLng(code) {
  if (!code) return null;
  return COUNTRY_LATLNG[String(code).trim().toUpperCase()] || null;
}
